import styled from 'styled-components';

import Star from '@/components/atom/icon/Star';
import { BookingAvailability } from '@/types/bff/gql-schema';
import { NonF2fDoctorVo } from '@/types/search';

interface Props {
  doctor: NonF2fDoctorVo;
  onClick: () => void;
}

const DoctorBookingStatusTime = ({ doctor }: Pick<Props, 'doctor'>) => {
  const { bookingAvailability, todaySchedule, tomorrowSchedule, isBookable } = doctor.bookingInfo;

  if (!isBookable) {
    return null;
  }

  switch (bookingAvailability) {
    case BookingAvailability.TodayBookable:
      return (
        <BookingInfoWrapper>
          <TodayBookable>오늘 예약</TodayBookable>
          <Divider />
          <DoctorSchedule>{todaySchedule}</DoctorSchedule>
        </BookingInfoWrapper>
      );
    case BookingAvailability.TomorrowBookable:
      return (
        <BookingInfoWrapper>
          <TomorrowBookable>내일 예약</TomorrowBookable>
          <Divider />
          <DoctorSchedule>{tomorrowSchedule}</DoctorSchedule>
        </BookingInfoWrapper>
      );
    case BookingAvailability.NotBookable:
    default:
      return null;
  }
};
const formatReviewCount = (count: number) => {
  if (count >= 2000) return '2000+';
  if (count >= 1500) return '1500+';
  if (count >= 1000) return '1000+';
  if (count >= 500) return '500+';
  if (count >= 300) return '300+';
  if (count >= 200) return '200+';
  if (count >= 100) return '100+';
  if (count >= 50) return '50+';
  if (count >= 20) return '20+';
  if (count >= 10) return '10+';
  return String(count);
};
const Doctor = ({ doctor, onClick }: Props) => {
  const isSpecialist = !!doctor.professionalDepartment;
  const isReadyForVideoRequest = doctor.readyForVideoCall;
  const hasTag = isSpecialist || isReadyForVideoRequest;
  const reviewAverage = Number(doctor?.reviewAverageRating)?.toFixed(1);
  const hasProfileUrl = !!doctor.profileUrl;
  const isNotAvailable =
    doctor.bookingInfo.bookingAvailability === BookingAvailability.NotBookable || !doctor.bookingInfo.isBookable;

  return (
    <Wrapper onClick={onClick}>
      <Content>
        <NameAndReview>
          <DoctorAndHospitalName>
            <DoctorName>{doctor.name}</DoctorName>
            <HospitalName>{doctor.hospitalV2.hospitalName}</HospitalName>
          </DoctorAndHospitalName>
          <StarAndReviewAndRating>
            <Star />
            <ReviewAndRating>
              <AverageRating>{reviewAverage}</AverageRating>
              <ReviewCount>({formatReviewCount(doctor.reviewCount)})</ReviewCount>
            </ReviewAndRating>
          </StarAndReviewAndRating>
        </NameAndReview>
        <DoctorBookingStatusTime doctor={doctor} />
        {hasTag && (
          <Tags>
            {isSpecialist && <ProfessionalTag>{doctor.professionalDepartment?.name}전문의</ProfessionalTag>}
            {isReadyForVideoRequest && <VideoTag>영상진료</VideoTag>}
          </Tags>
        )}
      </Content>
      <ProfileImageWrapper>
        {hasProfileUrl ? (
          <ProfileImage style={{ backgroundImage: `url("${doctor.profileUrl}")` }} />
        ) : (
          <DefaultProfileImage>{doctor.name}</DefaultProfileImage>
        )}
        {isNotAvailable && <ClosedOverlap>예약 마감</ClosedOverlap>}
      </ProfileImageWrapper>
    </Wrapper>
  );
};

export default Doctor;

const Wrapper = styled.div`
  display: flex;
  padding: 20px 24px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 36px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ProfileImage = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
`;

const DefaultProfileImage = styled.div`
  width: 100%;
  height: 100%;
  background: #5992ff66;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme }) => `
    ${theme.typography.UI22SB};
    color: ${theme.color.white};
  `};
`;

const ClosedOverlap = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(1, 1, 1, 0.5);
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme }) => `
    ${theme.typography.UI13SB};
    color: ${theme.color.white};
  `};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

const NameAndReview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const DoctorAndHospitalName = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const DoctorName = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => `
    ${theme.typography.UI17SB};
    color: ${theme.color.gray600};
  `}
`;

const HospitalName = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI13R};
    color: ${theme.color.gray200};
  `}
`;

const StarAndReviewAndRating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const ReviewAndRating = styled.div`
  display: flex;
  align-items: center;
`;

const AverageRating = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI13M};
    color: ${theme.color.gray600};
  `}
`;

const ReviewCount = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI13M};
    color: ${theme.color.gray300};
  `}
`;

const Tags = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
`;

const Tag = styled.div`
  display: flex;
  height: 20px;
  padding: 0px 4px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-radius: 4px;
`;

const VideoTag = styled(Tag)`
  ${({ theme }) => `
    background: ${theme.color.gray30};
    ${theme.typography.UI12M};
    color: ${theme.color.gray300};
  `}
`;
const ProfessionalTag = styled(Tag)`
  ${({ theme }) => `
    background: ${theme.color.blue100};
    ${theme.typography.UI12M};
    color: ${theme.color.blue600};
  `}
`;

const BookingInfoWrapper = styled.div`
  flex-direction: row;
  align-items: center;
  display: flex;
`;

const BookingStatus = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI13SB};
  `}
`;

const TodayBookable = styled(BookingStatus)`
  color: ${({ theme }) => theme.color.green700};
`;

const TomorrowBookable = styled(BookingStatus)`
  color: ${({ theme }) => theme.color.primary600};
`;

const Divider = styled.div`
  width: 1px;
  height: 10px;
  background-color: ${({ theme }) => theme.color.gray100};
  margin: 0 6px;
`;

const DoctorSchedule = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI13R};
    color: ${theme.color.gray300};
  `}
`;
//
// const PriceAndPoint = styled.div`
//   display: flex;
//   flex-direction: column;
//   justify-content: flex-end;
//   align-items: flex-start;
//   align-self: stretch;
// `;
//
// const PointRow = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 2px;
// `;
//
// const PointDescription = styled.div`
//   ${({ theme }) => `
//     ${theme.typography.UI11R};
//     color: ${theme.color.gray400};
//   `}
// `;
//
// const PointDiscountedPrice = styled.p`
//   ${({ theme }) => `
//     ${theme.typography.UI11R};
//     color: ${theme.color.gray200};
//   `}
//   text-decoration: line-through;
// `;
//
// const PriceRow = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 6px;
// `;
//
// const PillPrice = styled.div`
//   ${({ theme }) => `
//     ${theme.typography.UI18SB};
//     color: ${theme.color.gray700};
//   `}
// `;
//
// const PillNameAndUnit = styled.div`
//   ${({ theme }) => `
//     ${theme.typography.UI13M};
//     color: ${theme.color.gray300};
//   `}
// `;
